import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { quotesAPI, jobsAPI, aiAPI, emailAPI, brandingAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Search, Edit, Send, FileText, Trash2, Sparkles, Loader2, Download, Link, Copy, Eye, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { DatePicker } from '../components/DatePicker';
import { format } from 'date-fns';
import { PDFViewer } from '../components/ui/pdf-viewer';

const statusColors = {
  draft: 'status-draft',
  sent: 'status-sent',
  accepted: 'status-accepted',
  rejected: 'status-rejected',
  expired: 'status-cancelled',
};

export const QuotesPage = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfTitle, setPdfTitle] = useState('');
  const [pdfImportDialogOpen, setPdfImportDialogOpen] = useState(false);
  const [pdfImportJob, setPdfImportJob] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfImportLoading, setPdfImportLoading] = useState(false);
  const [formData, setFormData] = useState({
    job_id: '',
    client_name: '',
    client_email: '',
    items: [{ description: '', quantity: 1, unit_price: 0 }],
    notes: '',
    valid_until: '',
    charge_gst: true,
  });
  const [companyChargesGst, setCompanyChargesGst] = useState(true);

  useEffect(() => {
    fetchData();
    brandingAPI.get().then((res) => {
      const flag = res?.data?.charge_gst;
      const def = typeof flag === 'boolean' ? flag : true;
      setCompanyChargesGst(def);
      setFormData((p) => ({ ...p, charge_gst: def }));
    }).catch(() => {});
  }, []);

  const fetchData = async () => {
    try {
      const [quotesRes, jobsRes] = await Promise.all([
        quotesAPI.getAll(),
        jobsAPI.getAll()
      ]);
      setQuotes(quotesRes.data);
      setJobs(jobsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handlePdfImport = async () => {
    if (!pdfFile || !pdfImportJob) {
      toast.error('Please select a job and upload a PDF');
      return;
    }

    setPdfImportLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('file', pdfFile);
      formDataToSend.append('job_id', pdfImportJob);

      const response = await aiAPI.importQuotePdf(formDataToSend);
      
      if (response.data.status === 'success') {
        // Pre-fill the quote form with extracted data
        setFormData({
          job_id: response.data.job_id,
          client_name: response.data.client_name || '',
          client_email: response.data.client_email || '',
          items: response.data.items.length > 0 ? response.data.items : [{ description: '', quantity: 1, unit_price: 0 }],
          notes: response.data.notes || '',
          valid_until: '',
        });
        setPdfImportDialogOpen(false);
        setDialogOpen(true);
        toast.success('Quote data extracted! Please review and save.');
      } else {
        toast.info(response.data.message || 'PDF processed with partial results');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import PDF');
    } finally {
      setPdfImportLoading(false);
      setPdfFile(null);
      setPdfImportJob('');
    }
  };

  const handleJobChange = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    setFormData({
      ...formData,
      job_id: jobId,
      client_name: job?.client_name || '',
      client_email: job?.client_email || '',
    });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value;
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const handleAIGenerate = async () => {
    const job = jobs.find(j => j.id === formData.job_id);
    if (!job) {
      toast.error('Please select a job first');
      return;
    }

    setAiLoading(true);
    try {
      const response = await aiAPI.generateQuote({
        job_description: `${job.title}\n${job.description || ''}\nAddress: ${job.address}`,
        job_type: job.title
      });
      
      if (response.data.suggestion) {
        // Try to parse the AI response
        try {
          const parsed = JSON.parse(response.data.suggestion);
          if (Array.isArray(parsed)) {
            const newItems = parsed.map(item => ({
              description: item.description,
              quantity: item.quantity || 1,
              unit_price: item.unit_price || 0
            }));
            setFormData({ ...formData, items: newItems });
            toast.success('AI suggestions loaded!');
          }
        } catch {
          toast.info('AI suggestion generated - please review and adjust manually');
        }
      }
    } catch (error) {
      toast.error('Failed to generate AI suggestions');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingQuote) {
        await quotesAPI.update(editingQuote.id, formData);
        toast.success('Quote updated');
      } else {
        await quotesAPI.create(formData);
        toast.success('Quote created');
      }
      
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      await quotesAPI.update(quoteId, { status: newStatus });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDownloadPdf = async (quoteId, quoteNumber) => {
    try {
      const response = await quotesAPI.downloadPdf(quoteId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Quote-${quoteNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleCopyClientLink = (clientToken) => {
    const link = `${window.location.origin}/client/quote/${clientToken}`;
    navigator.clipboard.writeText(link);
    toast.success('Client link copied to clipboard');
  };

  const handleSendQuote = async (quote) => {
    if (!quote.client_email) {
      toast.error('No client email address');
      return;
    }
    
    const clientLink = `${window.location.origin}/client/quote/${quote.client_token}`;
    const htmlContent = `
      <h2>Quote from ConstructOne</h2>
      <p>Dear ${quote.client_name},</p>
      <p>Please find your quote <strong>${quote.quote_number}</strong> attached.</p>
      <p><strong>Total: $${quote.total?.toFixed(2)}</strong></p>
      <p><a href="${clientLink}" style="background-color: #F97316; color: white; padding: 12px 24px; text-decoration: none; border-radius: 4px;">View & Approve Quote</a></p>
      <p>Thank you for your business!</p>
      <p>ConstructOne Team</p>
    `;
    
    try {
      await emailAPI.send({
        recipient_email: quote.client_email,
        subject: `Quote ${quote.quote_number} from ConstructOne`,
        html_content: htmlContent,
        related_type: 'quote',
        related_id: quote.id
      });
      await quotesAPI.update(quote.id, { status: 'sent' });
      toast.success(`Quote sent to ${quote.client_email}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to send quote');
    }
  };

  const handleDeleteQuote = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;
    
    try {
      await quotesAPI.delete(quoteId);
      toast.success('Quote deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete quote');
    }
  };

  const handleDuplicateQuote = async (quoteId) => {
    try {
      const response = await quotesAPI.duplicate(quoteId);
      toast.success(`Quote duplicated! New quote: ${response.data.quote_number}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate quote');
    }
  };

  const resetForm = () => {
    setDialogOpen(false);
    setEditingQuote(null);
    setFormData({
      job_id: '',
      client_name: '',
      client_email: '',
      items: [{ description: '', quantity: 1, unit_price: 0 }],
      notes: '',
      valid_until: '',
      charge_gst: companyChargesGst,
    });
  };

  const handleViewPdf = async (quoteId, quoteNumber) => {
    try {
      const response = await quotesAPI.downloadPdf(quoteId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfTitle(`Quote ${quoteNumber}`);
      setPdfViewerOpen(true);
    } catch (error) {
      toast.error('Failed to load PDF');
    }
  };

  const filteredQuotes = quotes.filter(q =>
    q.quote_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.client_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="quotes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Quotes</h1>
          <p className="text-muted-foreground mt-1">Create and manage job quotes</p>
        </div>
        <div className="flex gap-2">
          {/* PDF Import — wizard */}
          <Button
            variant="outline"
            className="rounded-sm border-primary/50 hover:border-primary"
            onClick={() => navigate('/quotes/import-pdf')}
            data-testid="import-pdf-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Import PDF
          </Button>

          {/* New Quote Dialog */}
          <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button className="uppercase font-bold tracking-wide rounded-sm" data-testid="add-quote-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Quote
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingQuote ? 'Edit Quote' : 'Create New Quote'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Select Job *</Label>
                    <Select value={formData.job_id} onValueChange={handleJobChange}>
                      <SelectTrigger className="bg-input border-border rounded-sm" data-testid="quote-job">
                        <SelectValue placeholder="Select a job" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {jobs.map(job => (
                          <SelectItem key={job.id} value={job.id}>{job.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                <div className="space-y-2">
                    <Label>Valid Until</Label>
                    <DatePicker
                      value={formData.valid_until}
                      onChange={(d) => setFormData({ ...formData, valid_until: d })}
                      placeholder="Valid until"
                      data-testid="quote-valid-until"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Client Name *</Label>
                    <Input
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      required
                      className="bg-input border-border rounded-sm"
                      data-testid="quote-client-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Client Email</Label>
                    <Input
                      type="email"
                      value={formData.client_email}
                      onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                      className="bg-input border-border rounded-sm"
                    />
                  </div>
                </div>

                {/* Line Items */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Line Items</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleAIGenerate}
                        disabled={!formData.job_id || aiLoading}
                        className="rounded-sm"
                      >
                        {aiLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                        AI Suggest
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={addItem} className="rounded-sm">
                        <Plus className="h-4 w-4 mr-1" /> Add Item
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2 border border-border rounded-sm p-3">
                    {formData.items.map((item, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 items-center">
                        <Input
                          placeholder="Description"
                          value={item.description}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                          className="col-span-6 bg-input border-border rounded-sm text-sm"
                        />
                        <Input
                          type="number"
                          placeholder="Qty"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                          className="col-span-2 bg-input border-border rounded-sm text-sm"
                        />
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Price"
                          value={item.unit_price}
                          onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                          className="col-span-3 bg-input border-border rounded-sm text-sm"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeItem(index)}
                          className="col-span-1"
                          disabled={formData.items.length === 1}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex justify-end pt-2 border-t border-border">
                      <p className="font-mono font-bold">
                        Subtotal: ${calculateTotal().toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="bg-input border-border rounded-sm"
                    rows={3}
                    placeholder="Terms, conditions, or additional notes..."
                  />
                </div>

                <div className="flex items-center justify-between border border-border rounded-sm p-3">
                  <div className="flex items-center gap-3">
                    <Switch
                      id="quote-gst-toggle-page"
                      checked={formData.charge_gst}
                      onCheckedChange={(v) => setFormData({ ...formData, charge_gst: v })}
                      data-testid="quote-gst-toggle"
                    />
                    <Label htmlFor="quote-gst-toggle-page" className="cursor-pointer text-sm">
                      Charge GST {formData.charge_gst ? '(10%)' : '— not applicable'}
                    </Label>
                  </div>
                  <div className="text-right text-sm font-mono">
                    {formData.charge_gst ? (
                      <>
                        <div className="text-muted-foreground">GST: ${(calculateTotal() * 0.1).toFixed(2)}</div>
                        <div className="font-bold">Total: ${(calculateTotal() * 1.1).toFixed(2)}</div>
                      </>
                    ) : (
                      <div className="font-bold">Total: ${calculateTotal().toFixed(2)}</div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={resetForm} className="rounded-sm">Cancel</Button>
                  <Button type="submit" className="uppercase font-bold tracking-wide rounded-sm" data-testid="quote-submit">
                    {editingQuote ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search quotes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-input border-border rounded-sm"
          data-testid="quote-search"
        />
      </div>

      {/* Table */}
      <Card className="bg-card border-border rounded-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredQuotes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <FileText className="h-12 w-12 mb-4" />
              <p>No quotes found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>Quote #</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Valid Until</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredQuotes.map((quote) => (
                  <TableRow key={quote.id} className="border-border" data-testid={`quote-row-${quote.id}`}>
                    <TableCell className="font-mono text-sm">{quote.quote_number}</TableCell>
                    <TableCell>{quote.client_name}</TableCell>
                    <TableCell className="font-mono">${quote.total?.toFixed(2)}</TableCell>
                    <TableCell>
                      {quote.valid_until ? format(new Date(quote.valid_until), 'MMM d, yyyy') : '-'}
                    </TableCell>
                    <TableCell>
                      <Select value={quote.status} onValueChange={(v) => handleStatusChange(quote.id, v)}>
                        <SelectTrigger className={`w-auto h-auto px-2 py-1 border ${statusColors[quote.status]} rounded-sm text-xs font-mono uppercase`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="sent">Sent</SelectItem>
                          <SelectItem value="accepted">Accepted</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                          <SelectItem value="expired">Expired</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(quote.created_at), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" title="View PDF" onClick={() => handleViewPdf(quote.id, quote.quote_number)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Send Quote" onClick={() => handleSendQuote(quote)}>
                          <Send className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Download PDF" onClick={() => handleDownloadPdf(quote.id, quote.quote_number)}>
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Copy Client Link" onClick={() => handleCopyClientLink(quote.client_token)}>
                          <Link className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Duplicate Quote" onClick={() => handleDuplicateQuote(quote.id)}>
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Delete Quote" onClick={() => handleDeleteQuote(quote.id)} className="text-destructive hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* PDF Viewer Modal */}
      <PDFViewer
        isOpen={pdfViewerOpen}
        onClose={() => setPdfViewerOpen(false)}
        pdfUrl={pdfUrl}
        title={pdfTitle}
      />
    </div>
  );
};

export default QuotesPage;
